import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@vitness/icons';
import { cn } from '@vitness/theme';

/**
 * Modal dialog built on Radix Dialog. Provides focus trapping, scroll locking,
 * Escape-to-close, and overlay-click dismissal out of the box. Styling is fully
 * token-driven (`--vn-*`) so it adapts to dark/light themes automatically.
 *
 * Consumers compose the dialog from the exported parts:
 * `Dialog` (root), `DialogTrigger`, `DialogContent`, `DialogHeader`,
 * `DialogFooter`, `DialogTitle`, `DialogDescription`, and `DialogClose`.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export type DialogProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Root>;

/** Dimmed, blurred backdrop rendered behind dialog content. */
export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/55 backdrop-blur-sm',
        className,
      )}
      {...props}
    />
  );
});

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Renders the built-in top-right close button. Defaults to `true`. */
  showClose?: boolean;
  children?: ReactNode;
}

/**
 * Centered dialog surface. Returns focus to the trigger on close and traps
 * focus within while open (handled by Radix). The optional close button is
 * keyboard-reachable and labelled for assistive tech.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent({ className, children, showClose = true, ...props }, ref) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-md',
          'border border-border bg-surface-card text-text-primary',
          'rounded-vn-lg shadow-vn-card font-body',
          'p-vn-5 focus:outline-none',
          className,
        )}
        {...props}
      >
        {children}
        {showClose ? (
          <DialogPrimitive.Close
            aria-label="Close"
            className={cn(
              'absolute right-vn-3 top-vn-3 inline-flex h-6 w-6 items-center justify-center',
              'rounded-vn-sm text-text-muted',
              'hover:bg-surface-card-hover hover:text-text-primary',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-border-strong)]',
              'transition-colors',
            )}
          >
            <X size={15} />
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

/** Header block: title + optional description, stacked. */
export function DialogHeader({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-vn-1 pr-vn-6', className)}
      {...props}
    />
  );
}

/** Footer block: right-aligned action buttons by default. */
export function DialogFooter({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cn(
        'mt-vn-5 flex flex-col-reverse gap-vn-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'font-display text-base font-semibold leading-tight text-text-primary',
        className,
      )}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm leading-relaxed text-text-secondary', className)}
      {...props}
    />
  );
});
